import { useEffect, useRef, useState, useCallback } from 'react';
import { api } from '@/lib/api';
import { useNegocio, useOrderNotifications } from './AliadoDashboardLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ShoppingCart, RefreshCcw, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const POLL_INTERVAL = 30_000; // 30 seconds

const STATUS_FLOW = ['new', 'preparing', 'ready', 'delivered'];
const STATUS_CONFIG = {
    new: { label: 'Nuevo', variant: 'outline', next: 'preparing', nextLabel: 'Marcar preparando' },
    preparing: { label: 'Preparando', variant: 'secondary', next: 'ready', nextLabel: 'Marcar listo' },
    ready: { label: 'Listo', variant: 'default', next: 'delivered', nextLabel: 'Marcar entregado' },
    delivered: { label: 'Entregado', variant: 'default', next: null, nextLabel: null },
    cancelled: { label: 'Cancelado', variant: 'destructive', next: null, nextLabel: null },
};

const fmt = (n) => `$${Number(n || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function OrderCard({ order, onStatusChange }) {
    const [expanded, setExpanded] = useState(false);
    const [loading, setLoading] = useState(false);
    const cfg = STATUS_CONFIG[order.status] || { label: order.status, variant: 'outline', next: null };

    const advance = async () => {
        if (!cfg.next) return;
        setLoading(true);
        try {
            await onStatusChange(order.id, cfg.next);
        } finally {
            setLoading(false);
        }
    };

    const cancel = async () => {
        setLoading(true);
        try {
            await onStatusChange(order.id, 'cancelled');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="rounded-2xl border bg-card p-4 space-y-3">
            <div className="flex items-start justify-between gap-2">
                <div>
                    <div className="font-semibold text-sm">{order.customer_name}</div>
                    <div className="text-xs text-muted-foreground">{order.customer_phone} · {order.delivery_type === 'delivery' ? '🛵 Domicilio' : '🏪 Recoger'}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{new Date(order.created_at).toLocaleString('es-MX')}</div>
                </div>
                <div className="flex flex-col items-end gap-1">
                    <Badge variant={cfg.variant} className="text-[10px]">{cfg.label}</Badge>
                    <span className="text-sm font-bold">{fmt(order.total)}</span>
                </div>
            </div>

            <button onClick={() => setExpanded(e => !e)}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
                {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                {order.items?.length ?? 0} artículo(s)
            </button>

            {expanded && (
                <div className="space-y-1 bg-muted/30 rounded-lg p-2">
                    {(order.items || []).map((item, i) => (
                        <div key={i} className="flex justify-between text-xs">
                            <span>{item.qty}× {item.name}</span>
                            <span>{fmt(item.price * item.qty)}</span>
                        </div>
                    ))}
                    {order.delivery_cost > 0 && (
                        <div className="flex justify-between text-xs text-muted-foreground border-t pt-1 mt-1">
                            <span>Envío</span><span>{fmt(order.delivery_cost)}</span>
                        </div>
                    )}
                    {order.notes && <p className="text-xs text-muted-foreground italic mt-1">📝 {order.notes}</p>}
                </div>
            )}

            {order.status !== 'delivered' && order.status !== 'cancelled' && (
                <div className="flex gap-2 pt-1">
                    {cfg.next && (
                        <Button size="sm" onClick={advance} disabled={loading}
                            className="bg-orange-600 hover:bg-orange-700 text-xs h-7">
                            {cfg.nextLabel}
                        </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={cancel} disabled={loading}
                        className="text-xs h-7 text-destructive hover:bg-destructive/10">
                        Cancelar
                    </Button>
                </div>
            )}
        </div>
    );
}

export default function AliadoOrdersPage() {
    const negocio = useNegocio();
    const { setNewOrderCount } = useOrderNotifications();
    const [orders, setOrders] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState('__none__');
    const [showNewOrder, setShowNewOrder] = useState(false);
    const [newOrder, setNewOrder] = useState({ customer_name: '', customer_phone: '', payment_method: 'cash', delivery_type: 'pickup', notes: '', items: [], delivery_cost: 0 });
    const [saving, setSaving] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);

    // Track seen order IDs across polls (use ref so no re-render on update)
    const seenIds = useRef(null);

    const load = useCallback(async (isBackgroundPoll = false) => {
        if (!isBackgroundPoll) setLoading(true);
        try {
            const params = new URLSearchParams({ limit: '100' });
            if (filterStatus !== '__none__') params.set('status', filterStatus);
            const { data } = await api.get(`/me/oknegocio/orders?${params}`);
            const fetched = data.items || [];

            if (seenIds.current === null) {
                // First load — seed seen set, no notifications
                seenIds.current = new Set(fetched.map(o => o.id));
            } else if (isBackgroundPoll) {
                // Background poll — find genuinely new orders
                const incoming = fetched.filter(o => o.status === 'new' && !seenIds.current.has(o.id));
                if (incoming.length > 0) {
                    incoming.forEach(o => seenIds.current.add(o.id));
                    const n = incoming.length;
                    toast.success(`🛎️ ${n === 1 ? 'Nueva orden recibida' : `${n} órdenes nuevas`}`, {
                        description: incoming.map(o => o.customer_name).join(', '),
                        duration: 6000,
                    });
                    setUnreadCount(prev => {
                        const next = prev + n;
                        setNewOrderCount(next);
                        return next;
                    });
                }
                // Also seed any other new IDs we haven't tracked yet
                fetched.forEach(o => seenIds.current.add(o.id));
            }

            setOrders(fetched);
            setTotal(data.total || 0);
        } catch {
            if (!isBackgroundPoll) toast.error('Error al cargar órdenes');
        } finally {
            if (!isBackgroundPoll) setLoading(false);
        }
    }, [filterStatus, setNewOrderCount]);

    // Reset seen IDs when filter changes so we re-seed
    useEffect(() => {
        seenIds.current = null;
        load(false);
    }, [filterStatus]); // eslint-disable-line react-hooks/exhaustive-deps

    // Update document title badge
    useEffect(() => {
        if (unreadCount > 0) {
            document.title = `(${unreadCount}) Órdenes nuevas`;
        } else {
            document.title = 'Órdenes';
        }
        return () => { document.title = 'OkNegocios'; };
    }, [unreadCount]);

    // Reset unread when user views the page (mark as seen on manual refresh too)
    const handleManualRefresh = () => {
        setUnreadCount(0);
        setNewOrderCount(0);
        seenIds.current = null;
        load(false);
    };

    // Background polling
    useEffect(() => {
        const timer = setInterval(() => load(true), POLL_INTERVAL);
        return () => clearInterval(timer);
    }, [load]);

    const handleStatusChange = async (orderId, status) => {
        try {
            await api.put(`/me/oknegocio/orders/${orderId}/status`, { status });
            toast.success('Estado actualizado');
            load();
        } catch { toast.error('Error al actualizar estado'); }
    };

    const handleCreateOrder = async () => {
        if (!newOrder.customer_name) return toast.error('Ingresa el nombre del cliente');
        setSaving(true);
        try {
            const items = [{ name: 'Pedido manual', qty: 1, price: 0 }];
            const total = items.reduce((s, i) => s + i.price * i.qty, 0) + Number(newOrder.delivery_cost || 0);
            await api.post('/me/oknegocio/orders', { ...newOrder, items, subtotal: 0, total });
            toast.success('Orden creada');
            setShowNewOrder(false);
            setNewOrder({ customer_name: '', customer_phone: '', payment_method: 'cash', delivery_type: 'pickup', notes: '', items: [], delivery_cost: 0 });
            load();
        } catch { toast.error('Error al crear orden'); }
        finally { setSaving(false); }
    };

    const grouped = {
        new: orders.filter(o => o.status === 'new'),
        preparing: orders.filter(o => o.status === 'preparing'),
        ready: orders.filter(o => o.status === 'ready'),
        delivered: orders.filter(o => o.status === 'delivered'),
        cancelled: orders.filter(o => o.status === 'cancelled'),
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                    <h1 className="text-xl font-bold flex items-center gap-2"><ShoppingCart className="h-5 w-5 text-orange-600" /> Órdenes</h1>
                    <p className="text-sm text-muted-foreground">{total} orden(es) en total</p>
                </div>
                <div className="flex gap-2">
                    <Select value={filterStatus} onValueChange={setFilterStatus}>
                        <SelectTrigger className="w-36 h-8 text-xs">
                            <SelectValue placeholder="Todos los estados" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="__none__">Todos</SelectItem>
                            <SelectItem value="new">Nuevos</SelectItem>
                            <SelectItem value="preparing">Preparando</SelectItem>
                            <SelectItem value="ready">Listos</SelectItem>
                            <SelectItem value="delivered">Entregados</SelectItem>
                            <SelectItem value="cancelled">Cancelados</SelectItem>
                        </SelectContent>
                    </Select>
                    <Button size="sm" variant="outline" onClick={handleManualRefresh} className="h-8"><RefreshCcw className="h-3.5 w-3.5" /></Button>
                    <Button size="sm" onClick={() => setShowNewOrder(true)} className="h-8 bg-orange-600 hover:bg-orange-700 text-xs">+ Nueva orden</Button>
                </div>
            </div>

            {loading ? (
                <div className="text-sm text-muted-foreground py-8 text-center">Cargando órdenes…</div>
            ) : orders.length === 0 ? (
                <div className="rounded-2xl border bg-card p-8 text-center text-muted-foreground text-sm">
                    No hay órdenes{filterStatus !== '__none__' ? ' con este estado' : ''}.
                </div>
            ) : (
                <div className="grid gap-6">
                    {filterStatus === '__none__' ? (
                        // Kanban-style by status
                        STATUS_FLOW.concat(['cancelled']).filter(s => grouped[s]?.length > 0).map(status => (
                            <div key={status}>
                                <h2 className="text-sm font-semibold text-muted-foreground mb-2 uppercase tracking-wide">
                                    {STATUS_CONFIG[status]?.label} ({grouped[status].length})
                                </h2>
                                <div className="grid sm:grid-cols-2 gap-3">
                                    {grouped[status].map(o => (
                                        <OrderCard key={o.id} order={o} onStatusChange={handleStatusChange} />
                                    ))}
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="grid sm:grid-cols-2 gap-3">
                            {orders.map(o => <OrderCard key={o.id} order={o} onStatusChange={handleStatusChange} />)}
                        </div>
                    )}
                </div>
            )}

            {/* New order dialog */}
            <Dialog open={showNewOrder} onOpenChange={setShowNewOrder}>
                <DialogContent className="max-w-sm">
                    <DialogHeader><DialogTitle>Nueva orden manual</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <label className="text-xs font-medium">Nombre del cliente</label>
                            <Input value={newOrder.customer_name} onChange={e => setNewOrder(p => ({ ...p, customer_name: e.target.value }))} placeholder="Ej. Juan Pérez" className="mt-1" />
                        </div>
                        <div>
                            <label className="text-xs font-medium">Teléfono (opcional)</label>
                            <Input value={newOrder.customer_phone} onChange={e => setNewOrder(p => ({ ...p, customer_phone: e.target.value }))} placeholder="55 1234 5678" className="mt-1" />
                        </div>
                        <div>
                            <label className="text-xs font-medium">Tipo de entrega</label>
                            <Select value={newOrder.delivery_type} onValueChange={v => setNewOrder(p => ({ ...p, delivery_type: v }))}>
                                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="pickup">Recoger en tienda</SelectItem>
                                    <SelectItem value="delivery">Domicilio</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <label className="text-xs font-medium">Método de pago</label>
                            <Select value={newOrder.payment_method} onValueChange={v => setNewOrder(p => ({ ...p, payment_method: v }))}>
                                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="cash">Efectivo</SelectItem>
                                    <SelectItem value="card">Tarjeta</SelectItem>
                                    <SelectItem value="transfer">Transferencia</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <label className="text-xs font-medium">Notas (opcional)</label>
                            <Input value={newOrder.notes} onChange={e => setNewOrder(p => ({ ...p, notes: e.target.value }))} placeholder="Sin cebolla, extra salsa…" className="mt-1" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowNewOrder(false)}>Cancelar</Button>
                        <Button onClick={handleCreateOrder} disabled={saving} className="bg-orange-600 hover:bg-orange-700">
                            {saving ? 'Guardando…' : 'Crear orden'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
